import React, { useState, useEffect, useRef } from "react";
import { 
  Mic, MicOff, Video, VideoOff, Settings, Share2, 
  MessageSquare, Users, Heart, Send, Radio 
} from "lucide-react";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Card from "../../../components/ui/Card";

const LivestreamStudioPage = () => {
  const [isLive, setIsLive] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [cameraOn, setCameraOn] = useState(true);
  const [streamTitle, setStreamTitle] = useState("Chilling & Chatting with Fans! 🎵");
  const [viewers, setViewers] = useState(0);
  const [likes, setLikes] = useState(0);
  const [messages, setMessages] = useState([
    { id: 1, user: "Fan123", text: "Can't wait!", color: "text-blue-600" },
    { id: 2, user: "MusicLover", text: "Hi Gigi! 👋", color: "text-purple-600" },
  ]);
  const [newMessage, setNewMessage] = useState("");
  const chatEndRef = useRef(null);

  // Simulate chat and engagement when live
  useEffect(() => {
    let interval;
    if (isLive) {
      interval = setInterval(() => {
        // Randomly add viewers
        setViewers(prev => prev + Math.floor(Math.random() * 5));
        
        // Randomly add likes
        if (Math.random() > 0.3) {
          setLikes(prev => prev + Math.floor(Math.random() * 10));
        }

        // Randomly add messages
        if (Math.random() > 0.7) {
          const randomMsgs = ["Love this!", "Amazing song!", "Hello from Addis!", "🔥", "❤️"];
          const randomUser = `User${Math.floor(Math.random() * 1000)}`;
          const msg = {
            id: Date.now(),
            user: randomUser,
            text: randomMsgs[Math.floor(Math.random() * randomMsgs.length)],
            color: "text-slate-600"
          };
          setMessages(prev => [...prev, msg]);
        }
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [isLive]);

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    setMessages([...messages, { id: Date.now(), user: "You", text: newMessage, color: "text-primary font-bold" }]);
    setNewMessage("");
  };

  const toggleLive = () => {
    if (isLive) {
      if (window.confirm("End stream?")) {
        setIsLive(false);
        setViewers(0);
      }
    } else {
      setIsLive(true);
    }
  };

  return (
    <div className="h-[calc(100vh-6rem)] flex flex-col lg:flex-row gap-4 p-4 bg-slate-50">
      {/* Main Stream Area */}
      <div className="flex-1 flex flex-col gap-4 min-h-0">
        {/* Video Player / Preview */}
        <div className="relative flex-1 bg-black rounded-xl overflow-hidden flex items-center justify-center group">
          {cameraOn ? (
            <img 
              src="https://images.unsplash.com/photo-1516280440614-6697288d5d38?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80" 
              alt="Stream Preview" 
              className="w-full h-full object-cover opacity-80"
            />
          ) : (
            <div className="text-slate-500 flex flex-col items-center">
              <VideoOff size={48} className="mb-2" />
              <p>Camera is off</p>
            </div>
          )}
          
          {/* Overlay Controls */}
          <div className="absolute top-4 left-4 flex items-center gap-2">
            {isLive ? (
              <div className="bg-red-600 text-white px-3 py-1 rounded-md text-sm font-bold flex items-center animate-pulse">
                <Radio size={14} className="mr-2" /> LIVE
              </div>
            ) : (
              <div className="bg-slate-800/80 text-white px-3 py-1 rounded-md text-sm font-bold">
                OFFLINE
              </div>
            )}
            <div className="bg-black/50 text-white px-3 py-1 rounded-md text-sm flex items-center">
              <Users size={14} className="mr-2" /> {viewers.toLocaleString()}
            </div>
            <div className="bg-black/50 text-white px-3 py-1 rounded-md text-sm flex items-center">
              <Heart size={14} className="mr-2 text-pink-500" /> {likes.toLocaleString()}
            </div>
          </div>

          {/* Stream Title Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
             <h2 className="text-white text-2xl font-bold">{streamTitle}</h2>
          </div>
        </div>

        {/* Control Bar */}
        <Card className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setMicOn(!micOn)}
              className={`p-3 rounded-full transition-colors ${micOn ? 'bg-slate-100 hover:bg-slate-200 text-slate-700' : 'bg-red-100 text-red-600'}`}
            >
              {micOn ? <Mic size={20} /> : <MicOff size={20} />}
            </button>
            <button 
              onClick={() => setCameraOn(!cameraOn)}
              className={`p-3 rounded-full transition-colors ${cameraOn ? 'bg-slate-100 hover:bg-slate-200 text-slate-700' : 'bg-red-100 text-red-600'}`}
            >
              {cameraOn ? <Video size={20} /> : <VideoOff size={20} />}
            </button>
            <button className="p-3 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700">
              <Settings size={20} />
            </button>
            <button className="p-3 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700">
              <Share2 size={20} />
            </button>
          </div>

          <div className="flex items-center gap-4">
             <Input 
               value={streamTitle}
               onChange={(e) => setStreamTitle(e.target.value)}
               className="w-64"
               placeholder="Stream Title"
             />
             <Button 
               variant={isLive ? "destructive" : "primary"} 
               size="lg"
               onClick={toggleLive}
               className="min-w-[120px]"
             >
               {isLive ? "End Stream" : "Go Live"}
             </Button>
          </div>
        </Card>
      </div>

      {/* Chat Sidebar */}
      <div className="w-full lg:w-80 flex flex-col bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden h-[500px] lg:h-auto">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
          <h3 className="font-bold text-slate-700 flex items-center">
            <MessageSquare size={18} className="mr-2" /> Live Chat
          </h3>
          <Settings size={16} className="text-slate-400 cursor-pointer hover:text-slate-600" />
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-white">
          {messages.map((msg) => (
            <div key={msg.id} className="text-sm">
              <span className={`font-semibold mr-2 ${msg.color}`}>{msg.user}:</span>
              <span className="text-slate-700">{msg.text}</span>
            </div>
          ))}
          <div ref={chatEndRef} />
        </div>

        <div className="p-3 border-t border-slate-100 bg-slate-50">
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Say something..."
              className="flex-1 bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button 
              type="submit"
              className="p-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
              disabled={!newMessage.trim()}
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LivestreamStudioPage;
